import { formatCurrency } from "helpers";
import { FinanceSummary } from "services/api/reports/finance/types";
import {
  negative,
  positive,
  primary,
} from "ui/modules/dashboard/pages/DashboardPage/components/common/dashboardPalette";
import { KpiCardModel } from "./types";

/** Postotna promjena; prazna kad prethodno razdoblje nema od čega računati. */
const percentChange = (current: number, previous: number): number | null =>
  previous === 0 ? null : ((current - previous) / Math.abs(previous)) * 100;

const formatPercent = (value: number) => `${value.toFixed(1)} %`;

export const buildKpiCards = (summary: FinanceSummary): KpiCardModel[] => {
  const { totals, previous, months } = summary;

  const monthLabel = (index: number) => months[index].monthLabel || months[index].month;

  const points = (pick: (index: number) => number) =>
    months.map((_, index) => ({ label: monthLabel(index), value: pick(index) }));

  /** Marža po mjesecu; mjesec bez prihoda nema maržu, pa se vodi kao nula. */
  const monthlyMargin = (index: number) => {
    const month = months[index];

    return month.revenue > 0 ? (month.profit / month.revenue) * 100 : 0;
  };

  return [
    {
      label: "Prihodi",
      description: "Izdani računi u razdoblju",
      explanation:
        "Zbroj izlaznih računa s datumom u odabranom razdoblju. Ne ulaze predlošci, " +
        "otkazani računi ni storno stavke s negativnim iznosom.",
      value: formatCurrency.format(totals.revenue),
      valueClass: positive.text,
      change: percentChange(totals.revenue, previous.revenue),
      inverted: false,
      hex: positive.hex,
      points: points((i) => months[i].revenue),
      formatValue: (value) => formatCurrency.format(value),
    },
    {
      label: "Rashodi",
      description: "Nabava, troškovi i opći troškovi",
      explanation:
        "Nabavna vrijednost vozila kupljenih u razdoblju, ulazni računi i troškovi. " +
        "Nabava se uzima s kartice vozila, pa se trošak vrste „Nabava vozila” ne broji dvaput.",
      value: formatCurrency.format(totals.expense),
      valueClass: negative.text,
      change: percentChange(totals.expense, previous.expense),
      // Rast rashoda nije dobra vijest.
      inverted: true,
      hex: negative.hex,
      points: points((i) => months[i].expense),
      formatValue: (value) => formatCurrency.format(value),
    },
    {
      label: "Zarada",
      description: "Prihodi umanjeni za rashode",
      explanation:
        "Prihodi umanjeni za rashode. Opći troškovi bez vozila ovdje su uključeni, " +
        "za razliku od taba Po vozilu — zato se zbrojevi ta dva taba ne poklapaju.",
      value: formatCurrency.format(totals.profit),
      valueClass: totals.profit >= 0 ? positive.text : negative.text,
      change: percentChange(totals.profit, previous.profit),
      inverted: false,
      hex: primary.hex,
      points: points((i) => months[i].profit),
      formatValue: (value) => formatCurrency.format(value),
    },
    {
      label: "Marža",
      description: "Udio zarade u prihodima",
      explanation:
        "Zarada podijeljena prihodima. Kad u razdoblju nema prihoda, marža je nula. " +
        "Promjena se mjeri u postotnim bodovima, ne u postocima.",
      value: formatPercent(totals.marginPercent),
      valueClass: "text-slate-900",
      // Marža je već postotak, pa se uspoređuje razlikom u postotnim bodovima.
      change: totals.marginPercent - previous.marginPercent,
      inverted: false,
      hex: primary.hex,
      points: points(monthlyMargin),
      formatValue: formatPercent,
    },
  ];
};

/** Marža se mjeri u postotnim bodovima, ostalo u postocima. */
export const isPercentagePointMetric = (label: string) => label === "Marža";
